import React, { useMemo } from 'react';
import { roundQuantityWithThreshold } from '../../utils/forecastMath.js';

const DashboardTable = ({ data, filter, onFilterChange, stocks = {}, onStockChange }) => {
  const rows = useMemo(() => {
    if (!data?.groups) {
      return [];
    }
    return data.groups.flatMap((group) =>
      (group.items || []).map((item) => ({
        id: item.name,
        ...item,
        groupName: group.name
      }))
    );
  }, [data]);
  const computedRows = useMemo(() => {
    return rows.map((row) => {
      const isAzucarGlass = Boolean(row.isAzucarGlass);
      const stockValue = isAzucarGlass ? '' : (stocks[row.id] ?? '');
      const stock = parseInt(stockValue, 10);
      const safeStock = Number.isFinite(stock) && stock > 0 ? stock : 0;
      const forecastExact = Number.isFinite(row.forecastExact) ? row.forecastExact : (row.adjustedForecast || 0);
      const forecastRounded = Number.isFinite(row.forecastRounded)
        ? row.forecastRounded
        : (isAzucarGlass ? Number(forecastExact.toFixed(2)) : roundQuantityWithThreshold(forecastExact));
      const forecastDisplay = isAzucarGlass
        ? Number(forecastRounded).toFixed(2)
        : (row.forecastDisplay ?? forecastRounded);
      const toProduceDecimal = Math.max(0, forecastRounded - safeStock);
      const toProduceDisplay = isAzucarGlass
        ? toProduceDecimal.toFixed(2)
        : roundQuantityWithThreshold(toProduceDecimal);

      let status = 'ok';
      let statusText = 'En Stock';
      if (forecastRounded <= 0 && safeStock <= 0) {
        statusText = 'Sin demanda';
      } else if (toProduceDecimal > forecastRounded * 0.8) {
        status = 'critical';
        statusText = 'Crítico';
      } else if (toProduceDecimal > 0) {
        status = 'warning';
        statusText = 'Producir';
      }

      return {
        ...row,
        displayName: row.displayName || row.name,
        periodHtml: row.periodHtml || row.lifespan,
        periodTooltip: row.periodTooltip || '',
        isAzucarGlass,
        forecastExact,
        forecastRounded,
        forecastDisplay,
        prepMinutes: row.prepMinutes ?? 0,
        unitLabel: row.unitLabel || 'UNIDADES',
        stockValue: stockValue === undefined ? '' : stockValue,
        stock: safeStock,
        toProduceDecimal,
        toProduceDisplay,
        status,
        statusText
      };
    });
  }, [rows, stocks]);

  const filteredRows = useMemo(() => {
    if (!filter || filter === 'all') {
      return computedRows;
    }
    return computedRows.filter((row) => row.status === filter);
  }, [computedRows, filter]);

  if (!rows.length) {
    return (
      <div className="summary-empty">
        <div className="summary-empty-icon">🧠</div>
        <div className="summary-empty-text">Genera un pronóstico</div>
        <div className="summary-empty-subtext">Configura los parámetros para visualizar recomendaciones</div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-filters">
        <button
          className={`filter-btn${!filter || filter === 'all' ? ' active' : ''}`}
          onClick={() => onFilterChange?.('all')}
          type="button"
        >
          Todos
        </button>
        <button
          className={`filter-btn${filter === 'critical' ? ' active' : ''}`}
          onClick={() => onFilterChange?.('critical')}
          type="button"
        >
          Críticos
        </button>
        <button
          className={`filter-btn${filter === 'ok' ? ' active' : ''}`}
          onClick={() => onFilterChange?.('ok')}
          type="button"
        >
          En Stock
        </button>
      </div>
      <div className="dashboard-table-container">
        <table className="production-table react-table">
          <thead>
            <tr>
              <th className="col-article">Artículo</th>
              <th className="col-forecast">Pronóstico</th>
              <th className="col-stock">Stock Actual</th>
              <th className="col-produce">A Producir</th>
              <th className="col-time">Tiempo Prep.</th>
              <th className="col-status">Estado</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.id} className={`status-${row.status}`}>
            <td className="col-article">
              <div className="article-name">{row.displayName || row.name}</div>
              {row.periodHtml ? (
                <div
                  className="article-period"
                  dangerouslySetInnerHTML={{ __html: row.periodHtml }}
                  title={row.periodTooltip || ''}
                />
              ) : null}
            </td>
            <td
              className="col-forecast"
              title={`Pronóstico exacto: ${row.forecastExact.toFixed(row.isAzucarGlass ? 2 : 1)} unidades`}
            >
              {row.forecastDisplay}
            </td>
                <td className="col-stock">
                  {row.isAzucarGlass ? (
                    <span className="muted-text">--</span>
                  ) : (
                    <input
                      type="number"
                      className="stock-input"
                      value={row.stockValue}
                      placeholder="0"
                      min="0"
                      step="1"
                      onChange={(event) => onStockChange?.(row.id, event.target.value)}
                    />
                  )}
                </td>
                <td className="col-produce">
                  <span className={`produce-value ${row.toProduceDecimal > 0 ? 'produce-critical' : 'produce-ok'}`}>
                    {row.toProduceDisplay}
                  </span>
                </td>
                <td className="col-time">{row.prepMinutes} min</td>
                <td className="col-status">
                  <div className="status-container">
                    <span className={`status-badge status-${row.status}`}>{row.statusText}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DashboardTable;
